import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import numeral from 'numeral';

import { DEBOUNCE_TIMEOUT } from 'constants/Global';

/**
 * Regex for checking is a string looks like a positive regular integer (no scientific mark).
 * @type {RegExep}
 */
const POSITIVE_REGULAR_INTEGER_REGEX = /^[0-9.,]*$/;

/**
 * Convert given value according to type.
 * @param {String} type Input type
 * @param {any} value Input value
 * @returns Sanitized value
 */
function sanitizeValue(type, value) {
    if (type === 'number' && !Number.isFinite(value)) {
        return numeral(value.replace('.', ',').replace(' ', '')).value();
    }
    return value;
}

/**
 * Check if provided value is valid for type avec minimum.
 * @param {String} type Type of input
 * @param {Number} min Minimum value of input
 * @param {String} value Value to test
 * @return True if value is acceptable, false otherwise.
 */
export function isInputValid(type, min, value) {
    if (type === 'number' && min !== undefined) {
        if (min >= 0) {
            return POSITIVE_REGULAR_INTEGER_REGEX.test(value);
        }

        if (sanitizeValue(type, value) < min) {
            return false;
        }
    }
    return true;
}

/**
 * Basic input.
 * @param {String} type Type of the input (email, tel, text, number,...)
 * @param {Object} input Redux form input
 * @param {String} placeholder Placeholder of the input
 * @param {String} className Classname for the input
 * @param {number} min Minimum value of the input if type number
 */
export default class Default extends Component {
    /**
     * Creates an instance of Default.
     * @param {Object} props Given props.
     */
    constructor(props) {
        super(props);

        /**
         * React state of component.
         */
        this.state = {
            value: props.input.value,
        };

        this.onChange = this.onChange.bind(this);
    }

    /**
     * React lifecycle.
     * Update state value from given one
     */
    componentDidUpdate(prevProps) {
        const { input } = this.props;

        if (
            !this.state.value &&
            input.value !== this.state.value &&
            prevProps.input.value !== input.value
        ) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                value: input.value,
            });
        }
    }

    /**
     * Debounce onChange from user input for propagating change at end of input
     * @param {String} value User input value
     */
    onChange(value) {
        if (!isInputValid(this.props.type, this.props.min, value)) {
            return;
        }

        this.setState({ value });
        clearTimeout(this.onChangeTimeout);

        /**
         * Debounce timeout reference.
         */
        this.onChangeTimeout = setTimeout(
            () => this.props.input.onChange(sanitizeValue(this.props.type, this.state.value)),
            DEBOUNCE_TIMEOUT,
        );
    }

    /**
     * React lifecycle.
     */
    render() {
        const {
            type = 'text',
            input,
            meta: { active, touched, visited, error },
            placeholder,
            className,
            containerClassName,
            wrapperClassName,
            small,
            fullWidth,
            min = 0,
            children,
            hint,
            inputRef,
            ...rest
        } = this.props;

        const isError = (touched || visited) && error && (!active || (active && !!input.value));
        const showHint = !!hint && !isError;

        const containerClasses = classnames(containerClassName, {
            'field-container': true,
        });

        const wrapperClasses = classnames(wrapperClassName, {
            field: true,
            'field--active': active,
            'field--invalid': isError,
            'field--small': small,
            'field--full-width': fullWidth,
            'field--hinted': showHint,
        });

        const inputClasses = classnames(className, {
            field__control: true,
            'field__control--small': small,
        });

        return (
            <div className={containerClasses}>
                <div className={wrapperClasses}>
                    <input
                        className={inputClasses}
                        id={input.name}
                        type={type}
                        value={this.state.value}
                        placeholder={placeholder}
                        onFocus={input.onFocus}
                        onChange={(e) => this.onChange(e.target.value)}
                        onBlur={() => input.onBlur(sanitizeValue(type, this.state.value))}
                        min={type === 'number' ? min : ''}
                        lang={type === 'number' ? 'fr-FR' : ''}
                        ref={inputRef}
                        {...rest}
                    />
                    {children}
                </div>
                {isError && <p className="field-error">{error}</p>}
                {showHint && <div className="field-hint">{hint}</div>}
            </div>
        );
    }
}

Default.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
    hint: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.string,
    ]),
    className: PropTypes.string,
    input: PropTypes.shape({
        name: PropTypes.string,
        onChange: PropTypes.func.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    min: PropTypes.number,
    meta: PropTypes.shape({
        error: PropTypes.string,
        touched: PropTypes.bool,
        visited: PropTypes.bool,
    }),
    placeholder: PropTypes.string,
    type: PropTypes.string,
    wrapperClassName: PropTypes.string,
    small: PropTypes.bool,
    inputRef: PropTypes.func,
};

Default.defaultProps = {
    small: false,
};
